<template>
  <v-sheet
    color="white"
    elevation="5"
    rounded
    class="mt-5 mb-5 pt-3 pb-5 pl-3 pr-3"
    height="100%"
  >
    <v-overlay :value="loading">
      <v-progress-circular
        indeterminate
        color="primary"
        :size="70"
        :width="7"
      ></v-progress-circular>
    </v-overlay>
    <div>
      <v-row class="ma-0 mb-3">
        <h2>{{title}}</h2>
        <v-btn class="ml-2" color="primary" v-on:click="createNewAffiliate">+ New Affiliate</v-btn>
        <v-spacer></v-spacer>
        <v-btn color="primary" v-on:click="applyFilter">Apply Filters</v-btn>&nbsp;
        <v-btn color="secondary" v-on:click="clearAllFilters">Clear</v-btn>
      </v-row>
    </div>
    <PaginationSelect
      :totalCount="paginatedResponse.totalCount"
      :page="options.page"
      :size="options.itemsPerPage"
      v-on:load="load"
      v-on:changePage="options.page = $event"
      v-on:changeSize="options.itemsPerPage = $event"
    />
    <v-row>
      <v-alert width="100%" class="mr-5 ml-5" v-model="isError" dismissible type="error"> {{ errorMessage }}</v-alert>
    </v-row>
    <v-data-table
      dense
      disable-sort
      hide-default-footer
      class="elevation-1"
      :loading="loading"
      :headers="headers"
      :options.sync="options"
      :items="paginatedResponse.data"
      :server-items-length="paginatedResponse.totalCount"
      :footer-props="footerProps">
      <template v-slot:header.affiliateId="{ header }">
        <v-text-field v-model="searchParams.affiliateId"
          label="Affiliate ID"
          clearable
          @click:clear="searchParams.affiliateId = ''"
          v-on:keyup.enter="applyFilter"
        />
      </template>
      <template v-slot:header.affiliateName="{ header }">
        <v-text-field v-model="searchParams.affiliateName"
          label="Affiliate Name"
          clearable
          @click:clear="searchParams.affiliateName = ''"
          v-on:keyup.enter="applyFilter"
        />
      </template>
      <template v-slot:header.state="{ header }">        
        <v-combobox v-model="searchParams.state"
          :items="affiliateHomeStates"
          label="Home State"
          v-on:keyup.enter="applyFilter"
          clearable>
        </v-combobox>
      </template>
      <template v-slot:header.affiliateRep="{ header }">
        <div>
          <v-text-field v-model="searchParams.affiliateRep"
            label="Affiliate Rep Name/ID"
            clearable
            @click:clear="searchParams.affiliateRep = ''"
            v-on:keyup.enter="applyFilter"
          />
        </div>
      </template>
      <template v-slot:header.membershipStatus="{ header }">
        <div>
          <v-checkbox
            :indeterminate="searchParams.membershipStatus == null"
            v-model="searchParams.membershipStatus"
            label="Active"
            v-on:change="toggleCheckbox"
        />
        </div>
      </template>
      <template v-slot:header.affiliatePresident="{ header }">
        <div>
          <v-text-field v-model="searchParams.affiliatePresident"
            label="Affiliate President Name/ID"
            clearable
            @click:clear="searchParams.affiliatePresident = ''"
            v-on:keyup.enter="applyFilter"
          />
        </div>
      </template>
      <template v-slot:item.affiliateId="{ item }">
        <router-link :to="`/admin/affiliates/${item.affiliateId}`" target="_blank">
          {{item.affiliateId}}
        </router-link>
      </template>
      <template v-slot:item.affiliateName="{ item }">
        <router-link :to="`/admin/affiliates/${item.affiliateId}`" target="_blank">
          {{item.affiliateName}}
        </router-link>
      </template>
      <template v-slot:item.membershipStatus="{ item }">
        <span>
          <v-chip v-if="item.membershipExpiryDate"
            class="my-2"
            :color="new Date(item.membershipExpiryDate) < new Date() ? 'red' : 'green'"
            text-color="white">
            <v-avatar left>
              <v-icon v-if="new Date(item.membershipExpiryDate) < new Date()">mdi-close-circle-outline</v-icon>
              <v-icon v-else>mdi-checkbox-marked-circle</v-icon>
            </v-avatar>
            {{(new Date(item.membershipExpiryDate) < new Date()) ? "Expired" : "Active"}}
          </v-chip>
        </span>
      </template>
      <template v-slot:item.membershipExpiryDate="{ item }">
        <span>{{ item.membershipExpiryDate ? $dateUtils.formatDate(new Date(item.membershipExpiryDate)) : "" }}</span>
      </template>
      <template v-slot:item.affiliateRep="{ item }">
        <span>
          <router-link :to="`/admin/people/${item.affiliateRepPersonId}`" target="_blank">
            {{item.affiliateRepName}}
          </router-link>
        </span>
      </template>
      <template v-slot:item.affiliatePresident="{ item }">
        <span v-if="item.affiliatePresidentName">
          <router-link :to="`/admin/people/${item.affiliatePresidentPersonId}`" target="_blank">
            {{item.affiliatePresidentName}}
            </router-link>
        </span>
      </template>
    </v-data-table>
  </v-sheet>
</template>

<script lang="ts">
import Vue from "vue";
import CoownersList from "@/components/common/CoownersList.vue";
import PaginationSelect from "@/components/common/PaginationSelect.vue";

import affiliateService from "@/services/admin/affiliateService";

import { MetaNames } from "@/constants";
import { UserInfo } from "@/types/User";
import { PaginationOptions, PaginatedResponse } from "@/types/Pagination";

export default Vue.extend({
  name: "AffiliateList",
  components: {
    CoownersList,
    PaginationSelect
  },
  data: function () {
    return {
      title: MetaNames.AdminAffiliates,      
      affiliateHomeStates: [] as any[],
      loading: false,
      isError: false,
      errorMessage: "",
      paginatedResponse: {} as PaginatedResponse<object>,
      searchParams: {
        affiliateId: "",
        affiliateName: "",
        affiliateRep: "",
        state: "",
        affiliatePresident: "",
        membershipStatus: null as any,
      },
      membershipStatusFilterIdx: 0,
      options: {} as PaginationOptions,
      headers: [
        { text: "", align: "left", value: "affiliateId", width: "10%" },
        { text: "", align: "left", value: "affiliateName", width: "15%" },
        { text: "", align: "left", value: "state", width: "10%" },
        { text: "", align: "left", value: "affiliateRep", width: "25%" },
        { text: "Affiliate President", align: "left", value: "affiliatePresident", width: "25%" },
        { text: "Member Status", align: "left", value: "membershipStatus" },
        { text: "Member Expiry Date", align: "left", value: "membershipExpiryDate" },
      ],
      footerProps: { 'items-per-page-options': [10, 20, 30, 40, 50, 100] },
    };
  },
  created: function () {    
    affiliateService.getAffiliateHomeStates().then((res: any) => {
          this.affiliateHomeStates = res.states;
    });
    this.load();
  },
  watch: {
    options: {
      handler() {
        this.load();
      },
      deep: true,
    }
  },
  methods: {
    toggleCheckbox: function () {
      var membershipStatusStates = [null, true, false];
      var next = ++this.membershipStatusFilterIdx % membershipStatusStates.length;
      this.membershipStatusFilterIdx = next;
      this.searchParams.membershipStatus = membershipStatusStates[next];
    },
    createNewAffiliate: function () {
      this.$router.push({ path: '/admin/affiliates' });
    },
    clearAllFilters: function () {
      this.options.page = 1;
      this.options.itemsPerPage = 10;
      this.searchParams = {
        affiliateId: "",
        affiliateName: "",
        affiliateRep: "",
        state: "",
        affiliatePresident: "",
        membershipStatus: null as any,
      };
      this.membershipStatusFilterIdx = 0;
    },
    load: async function () {
      this.loading = true;

      const {
        page,
        itemsPerPage,
      } = this.options;

      try {
        const response = await affiliateService.getAffiliatesList({
          ...this.options,
          page: (page ?? 1) - 1,
          itemsPerPage: itemsPerPage ?? 10
        }, this.searchParams);

        this.loading = false;
        this.paginatedResponse = response;
      } catch (ex: any) {
        this.loading = false;
        this.isError = true;

        const errors = (ex?.response ?? ex)?.data?.errors;
        const descriptiveMessage = errors
          ? `Error(s): ${Object.values(errors).join(" & ")}`
          : null

        this.errorMessage = descriptiveMessage ?? ex?.message ?? "Unknown error occurred when retrieving data.";
      }
    },
    applyFilter() {
      this.options.page = 1;
      this.options.itemsPerPage = 10;

      this.load();
    },
  },
  computed: {
    userInfo(): UserInfo {
      return this.$store.state.userInfo;
    },
  },
});
</script>
